import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Rocket, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  TrendingUp,
  Users,
  Mail,
  DollarSign,
  Target,
  AlertCircle,
  ArrowRight,
  Play,
  Pause,
  BarChart3,
  Calendar,
  Clock,
  Zap,
  Activity,
  Edit,
  Trash2,
  Copy,
  Settings,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  Sparkles,
  Award,
  CheckCircle,
  XCircle,
  Eye,
  RotateCcw
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';

// Sequence interface
interface Sequence {
  id: string;
  name: string;
  description: string;
  category: 'sales' | 'nurture' | 'onboarding' | 'follow-up';
  status: 'active' | 'paused' | 'draft' | 'archived';
  steps: number;
  channels: ('email' | 'linkedin' | 'sms' | 'call')[];
  performance: {
    totalSent: number;
    responseRate: number;
    conversionRate: number;
    engagementScore: number;
  };
  createdAt: string;
  updatedAt: string;
  author: string;
}

interface SequenceStats {
  totalSequences: number;
  activeSequences: number;
  totalSent: number;
  avgResponseRate: number;
  avgConversionRate: number;
  topPerforming: number;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = ""
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
}> = ({ title, value, change, icon: Icon, gradient, description }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Sequence Card Component
const SequenceCard: React.FC<{
  sequence: Sequence;
  onEdit: (sequence: Sequence) => void;
  onView: (sequence: Sequence) => void;
  onDuplicate: (sequence: Sequence) => void;
  onDelete: (sequence: Sequence) => void;
}> = ({ sequence, onEdit, onView, onDuplicate, onDelete }) => {
  const [showActions, setShowActions] = useState(false);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          color: 'text-green-400 bg-green-500/20 border-green-500/30',
          dot: 'bg-green-400',
          label: 'Active'
        };
      case 'paused':
        return {
          color: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
          dot: 'bg-orange-400',
          label: 'Paused'
        };
      case 'draft':
        return {
          color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30',
          dot: 'bg-neutral-400',
          label: 'Draft'
        };
      default:
        return {
          color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30',
          dot: 'bg-neutral-400',
          label: 'Archived'
        };
    }
  };

  const getCategoryConfig = (category: string) => {
    switch (category) {
      case 'sales':
        return { color: 'text-blue-400', bg: 'bg-blue-500/20' };
      case 'nurture':
        return { color: 'text-green-400', bg: 'bg-green-500/20' };
      case 'onboarding':
        return { color: 'text-purple-400', bg: 'bg-purple-500/20' };
      default:
        return { color: 'text-orange-400', bg: 'bg-orange-500/20' };
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return '📧';
      case 'linkedin': return '💼';
      case 'sms': return '💬';
      case 'call': return '📞';
      default: return '📧';
    }
  };

  const statusConfig = getStatusConfig(sequence.status);
  const categoryConfig = getCategoryConfig(sequence.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/40 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white mb-1 truncate">
            {sequence.name}
          </h3>
          <p className="text-neutral-400 text-sm mb-2 line-clamp-2">
            {sequence.description}
          </p>
          <div className="flex items-center space-x-2 mb-2">
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              statusConfig.color
            )}>
              <div className="w-2 h-2 rounded-full bg-current mr-2" />
              {statusConfig.label}
            </div>
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
              categoryConfig.color,
              categoryConfig.bg
            )}>
              {sequence.category}
            </div>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-2 w-48 bg-neutral-900/95 backdrop-blur-xl border border-neutral-700 rounded-xl shadow-2xl z-10"
              >
                <div className="p-2">
                  <button
                    onClick={() => onView(sequence)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">View Details</span>
                  </button>
                  <button
                    onClick={() => onEdit(sequence)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Edit className="w-4 h-4" />
                    <span className="text-sm">Edit Sequence</span>
                  </button>
                  <button
                    onClick={() => onDuplicate(sequence)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Copy className="w-4 h-4" />
                    <span className="text-sm">Duplicate</span>
                  </button>
                  <div className="border-t border-neutral-700 my-1" />
                  <button
                    onClick={() => onDelete(sequence)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">Delete</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Sequence Flow Visualization */}
      <div className="mb-4 p-3 bg-neutral-800/30 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-neutral-400">Flow ({sequence.steps} steps)</span>
          <span className="text-xs text-neutral-400">{sequence.channels.length} channels</span>
        </div>
        <div className="flex items-center space-x-1">
          {sequence.channels.slice(0, 4).map((channel, index) => (
            <div
              key={index}
              className="w-6 h-6 bg-neutral-700 rounded-full flex items-center justify-center text-xs"
              title={channel}
            >
              {getChannelIcon(channel)}
            </div>
          ))}
          {sequence.channels.length > 4 && (
            <span className="text-xs text-neutral-400">+{sequence.channels.length - 4}</span>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-green-400">
            {sequence.performance.responseRate}%
          </p>
          <p className="text-xs text-neutral-400">Response</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-blue-400">
            {sequence.performance.conversionRate}%
          </p>
          <p className="text-xs text-neutral-400">Conversion</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-purple-400">
            {sequence.performance.engagementScore}
          </p>
          <p className="text-xs text-neutral-400">Engagement</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>By {sequence.author}</span>
        <span>{new Date(sequence.updatedAt).toLocaleDateString()}</span>
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

export const SequencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [stats, setStats] = useState<SequenceStats>({
    totalSequences: 0,
    activeSequences: 0,
    totalSent: 0,
    avgResponseRate: 0,
    avgConversionRate: 0,
    topPerforming: 0
  });

  const loadSequences = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockSequences: Sequence[] = [
        {
          id: 'seq-001',
          name: 'B2B Sales Outreach',
          description: 'Multi-touch sequence for B2B lead generation with email, LinkedIn, and follow-up calls',
          category: 'sales',
          status: 'active',
          steps: 7,
          channels: ['email', 'linkedin', 'email'],
          performance: {
            totalSent: 1250,
            responseRate: 23.4,
            conversionRate: 12.8,
            engagementScore: 87
          },
          createdAt: '2024-01-15',
          updatedAt: '2024-01-20',
          author: 'Sales Team'
        },
        {
          id: 'seq-002',
          name: 'Warm Lead Nurture',
          description: 'Gentle nurturing sequence for qualified leads with educational content and soft CTAs',
          category: 'nurture',
          status: 'active',
          steps: 5,
          channels: ['email', 'email', 'linkedin'],
          performance: {
            totalSent: 890,
            responseRate: 31.2,
            conversionRate: 18.5,
            engagementScore: 92
          },
          createdAt: '2024-01-18',
          updatedAt: '2024-01-22',
          author: 'Marketing Team'
        },
        {
          id: 'seq-003',
          name: 'Customer Onboarding',
          description: 'Welcome series for new customers with setup guides and success tips',
          category: 'onboarding',
          status: 'active',
          steps: 4,
          channels: ['email', 'email', 'sms'],
          performance: {
            totalSent: 567,
            responseRate: 45.8,
            conversionRate: 67.3,
            engagementScore: 95
          },
          createdAt: '2024-01-20',
          updatedAt: '2024-01-24',
          author: 'Customer Success'
        }
      ];
      
      setSequences(mockSequences);
    } catch (error) {
      console.error('Failed to load sequences:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Simulate API call
      const mockStats: SequenceStats = {
        totalSequences: 3,
        activeSequences: 3,
        totalSent: 2707,
        avgResponseRate: 33.5,
        avgConversionRate: 32.9,
        topPerforming: 1
      };
      setStats(mockStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadSequences();
    loadStats();
  }, []);

  const handleCreateSequence = () => {
    navigate('/sequences/builder');
  };

  const handleEdit = (sequence: Sequence) => {
    navigate(`/sequences/builder?edit=${sequence.id}`);
  };

  const handleView = (sequence: Sequence) => {
    console.log('View sequence:', sequence.name);
  };

  const handleDuplicate = (sequence: Sequence) => {
    console.log('Duplicate sequence:', sequence.name);
  };

  const handleDelete = (sequence: Sequence) => {
    console.log('Delete sequence:', sequence.name);
  };

  const filteredSequences = sequences.filter(sequence => {
    const matchesSearch = sequence.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sequence.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || sequence.status.toLowerCase() === statusFilter.toLowerCase();
    const matchesCategory = categoryFilter === 'All' || sequence.category.toLowerCase() === categoryFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const statusCounts = {
    All: sequences.length,
    Active: sequences.filter(s => s.status === 'active').length,
    Paused: sequences.filter(s => s.status === 'paused').length,
    Draft: sequences.filter(s => s.status === 'draft').length,
    Archived: sequences.filter(s => s.status === 'archived').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Sequences
            </h1>
            <p className="text-xl text-neutral-400">
              Build and manage multi-channel outreach sequences
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <Download className="w-5 h-5 mr-2" />
              Export
            </Button>
            <Button 
              size="lg"
              onClick={handleCreateSequence}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Sequence
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Total Sequences"
            value={stats.totalSequences}
            change="+3 this month"
            icon={Rocket}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="All sequence templates"
          />
          <MetricCard
            title="Active Sequences"
            value={stats.activeSequences}
            change="All running"
            icon={Activity}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Currently executing"
          />
          <MetricCard
            title="Total Sent"
            value={stats.totalSent.toLocaleString()}
            change="+450 this week"
            icon={Mail}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="Messages across sequences"
          />
          <MetricCard
            title="Avg Response Rate"
            value={`${stats.avgResponseRate.toFixed(1)}%`}
            change="+8.2% vs industry"
            icon={Target}
            gradient="bg-gradient-to-r from-orange-500 to-red-500"
            description="Across all sequences"
          />
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 mb-8 shadow-xl"
        >
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sequences..."
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-6">
              {/* Status Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      statusFilter === status
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {status}
                    {count > 0 && (
                      <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Category Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {['All', 'Sales', 'Nurture', 'Onboarding', 'Follow-up'].map((category) => (
                  <button
                    key={category}
                    onClick={() => setCategoryFilter(category)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      categoryFilter === category
                        ? 'bg-purple-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Sequences Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-400">Loading sequences...</p>
            </div>
          </div>
        ) : filteredSequences.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-3xl font-semibold text-white mb-4">No sequences found</h3>
            <p className="text-neutral-400 mb-8 max-w-md mx-auto">
              {searchQuery 
                ? 'Try adjusting your search criteria or filters' 
                : 'Start by creating your first sequence to automate your outreach'
              }
            </p>
            <Button
              size="lg"
              onClick={handleCreateSequence}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-6 h-6 mr-3" />
              Create Sequence
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {filteredSequences.map((sequence, index) => (
              <motion.div
                key={sequence.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <SequenceCard
                  sequence={sequence}
                  onEdit={handleEdit}
                  onView={handleView}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}; 